import type {
    RibbonControlGroupDefinitionMLR,
    RibbonTabDefinitionBase,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { constructKeytip, getKeytipString, Multiline } from 'owa-command-ribbon';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import { getMailRibbonConfigStore } from 'owa-mail-ribbon-store';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { FormatControlId, getStringRibbonId, MailRibbonTabId } from 'owa-ribbon-ids';
import { getInitialScalingSteps } from '../../../util/getInitialScalingSteps';
import { getMLRScalingSteps } from '../../../util/getScalingSteps';
import { getMLRTabGroups } from '../../../util/getTabGroups';
import { getMLRFormatTabCommonGroupFlyout } from './getMLRFormatTabCommonGroupFlyout';
import { getMLRFormatTabControlDefinition } from './getMLRFormatTabControlDefinition';
import { getMLRFormatTabGroupLayouts } from './getMLRFormatTabGroupLayouts';

const emptyCtrlList: FormatControlId[] = [
    FormatControlId.Bold,
    FormatControlId.Italic,
    FormatControlId.Underline,
    FormatControlId.StrikeThrough,
    FormatControlId.Bullets,
    FormatControlId.Numbering,
    FormatControlId.Indent,
    FormatControlId.Outdent,
    FormatControlId.AlignLeft,
    FormatControlId.AlignCenter,
    FormatControlId.AlignRight,
    FormatControlId.Subscript,
    FormatControlId.Superscript,
    FormatControlId.RemoveFormat,
    FormatControlId.FontNamePicker,
    FormatControlId.FontSizePicker,
    FormatControlId.ChangeCase,
    FormatControlId.FontColorPicker,
    FormatControlId.FontBackColorPicker,
];

const getMLRFormatTab = computedFn(
    (props: RibbonControlDefinitionProps): RibbonTabDefinitionBase => {
        return {
            type: Multiline,
            id: getStringRibbonId(MailRibbonTabId.Tab_Format),
            title: loc(Strings.format_tab),
            controlGroups: { type: Multiline, groups: getFormatTabGroups(props) },
            initialScalingSteps: getInitialScalingSteps(
                getMailRibbonConfigStore.multiline.formatTab,
                emptyCtrlList /*controlsWithoutLabels*/,
                props.popoutComposeViewState || getMainWindowComposeViewState() /*viewState*/
            ),
            scalingSteps: getMLRScalingSteps(MailRibbonTabId.Tab_Format),
            keytipProps: isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip(
                      [],
                      getKeytipString(MailRibbonTabId.Tab_Format),
                      true /* hasMenu */
                  )
                : undefined,
        };
    }
);
export default getMLRFormatTab;

/**
 * Retrieves the groups within the Format tab, as well as the actions that fall under each group.
 * @returns an object that contains the sub-groups and corresponding actions of the Format tab
 */
const getFormatTabGroups = computedFn(
    (props: RibbonControlDefinitionProps): RibbonControlGroupDefinitionMLR[] => {
        let formatTabGroups: RibbonControlGroupDefinitionMLR[] = new Array();
        formatTabGroups = getMLRTabGroups(
            MailRibbonTabId.Tab_Format,
            getMailRibbonConfigStore.multiline.formatTab.layout,
            getMLRFormatTabControlDefinition,
            getMLRFormatTabGroupLayouts,
            getMLRFormatTabCommonGroupFlyout,
            props
        );
        return formatTabGroups;
    }
);
